import {AfterViewChecked, Component, EventEmitter, HostListener, Input, OnDestroy, Output} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {map} from 'rxjs/operators';
import {ModalService} from '../modal.service';

interface ElementWithTabIndex {
  element: HTMLElement;
  tabIndex: number;
}

@Component({
  selector: 'modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalComponent implements OnDestroy, AfterViewChecked {
  @Input() dismissible = true;
  @Output('show') onShow = new EventEmitter<void>();
  @Output('hide') onHide = new EventEmitter<void>();

  visible$ = new BehaviorSubject(false);
  styles$ = this.visible$.pipe(map(v => ModalComponent.getStyles(v)));

  readonly elementsWithTabIndexQuerySelector = 'a, button, input, :not(a, button, input)[tabindex]';
  readonly modalElementClassName = 'reveal-modal';

  modalElement: HTMLElement = null;
  previousActiveElement: HTMLElement;
  elementsWithTabIndexList: ElementWithTabIndex[] = null;
  newFocusHasBeenSet = false;
  active = false;

  constructor(private ModalService: ModalService) {
  }

  private static getStyles(visible: boolean) {
    return {
      display: visible ? 'block' : 'none',
      visibility: visible ? 'visible' : 'hidden'
    };
  }

  private static makeElementsUnfocusable(listOfFocusableElements: Array<HTMLElement>): void {
    listOfFocusableElements.forEach((element) => {
      element.tabIndex = -1;
    });
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  @HostListener('window:keyup.escape', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (this.active) { this.hide(event); }
  }

  ngAfterViewChecked(): void {
    if (this.active) {
      this.changeFocusIntoModal();
    } else if (this.newFocusHasBeenSet) {
      this.restoreFocusToPrevious();
    }
  }

  ngOnDestroy(): void {
    this.ModalService.hide$.next(this);
  }

  show(): void {
    this.setPreviousActiveElement();
    this.makeElementsUnfocusable();
    this.active = true;
    this.visible$.next(true);
    this.ModalService.show$.next(this);
    this.onShow.emit();
  }

  backgroundClicked(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.dismissible) {
      this.hide(event);
    }
  }

  hide(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.active = false;
    this.visible$.next(false);
    this.ModalService.hide$.next(this);
    this.onHide.emit();
  }

  private changeFocusIntoModal(): void {
    const modalElementList = document.getElementsByClassName(this.modalElementClassName);

    if (modalElementList.length === 1 && this.newFocusHasBeenSet === false) {
      const modalElement: HTMLElement = modalElementList[0] as HTMLElement;
      modalElement.tabIndex = 0;
      modalElement.focus();
      this.newFocusHasBeenSet = true;
      this.modalElement = modalElement;
    }
  }

  private restoreFocusToPrevious(): void {
    this.makeElementsFocusable();
    this.previousActiveElement.focus();
    this.previousActiveElement = null;
    this.newFocusHasBeenSet = false;
    this.modalElement.tabIndex = -1;
    this.modalElement = null;
  }

  private makeElementsUnfocusable(): void {
    const focusableElementsList = this.getListOfFocusableElements();
    this.elementsWithTabIndexList = this.parseTabIndexFromElementList(focusableElementsList);
    ModalComponent.makeElementsUnfocusable(focusableElementsList);
  }

  private makeElementsFocusable(): void {
    this.elementsWithTabIndexList.forEach((originalElementState: ElementWithTabIndex) => {
      const element = originalElementState.element;
      element.tabIndex = originalElementState.tabIndex;
    });
    this.elementsWithTabIndexList = [];
  }

  private setPreviousActiveElement(): void {
    this.previousActiveElement = document.activeElement as HTMLElement;
  }

  private getListOfFocusableElements(): Array<HTMLElement> {
    return Array.from(document.querySelectorAll(this.elementsWithTabIndexQuerySelector));
  }

  private parseTabIndexFromElementList(elementList: Array<HTMLElement>): Array<ElementWithTabIndex> {
    const elementsWithTabIndexList = [];
    elementList.forEach((element) => {
      elementsWithTabIndexList.push(
        {element: element, tabIndex: element.tabIndex}
      );
    });
    return elementsWithTabIndexList;
  }
}
